// Unified Cart Add-ons System v2 - Ribbon, Gift Wrap, Wall Strips, and Bundle
// Handles ribbon color selection and bundle pricing logic

// Add missing fetchConfig function if not already defined globally
if (typeof fetchConfig === 'undefined') {
  window.fetchConfig = function(type = 'json') {
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': `application/${type}`,
        'X-Requested-With': 'XMLHttpRequest'
      }
    };
  };
}

class CartAddonV2 extends HTMLElement {
  // Static lock to prevent concurrent cart operations across all instances
  static cartOperationInProgress = false;
  static pendingOperation = null;

  constructor() {
    super();

    // Common properties
    this.miniCart = document.querySelector('mini-cart');
    this.cartItemsSize = parseInt(this.getAttribute('cart-items-size') || '0');
    this.itemsInCart = parseInt(this.getAttribute('items-in-cart') || '0');
    this.isMiniCart = this.closest('.mini-cart') !== null || this.closest('form#cart') !== null;
    
    // Addon type detection: 'ribbon', 'gift-wrap', 'wall-strips'
    this.addonType = this.getAttribute('addon-type') || 'gift-wrap';
    this.isRibbon = this.addonType === 'ribbon';
    this.isGiftWrap = this.addonType === 'gift-wrap';
    this.isWallStrips = this.addonType === 'wall-strips';
    
    // Bundle configuration
    this.bundleId = this.dataset.bundleId; // Bundle variant ID for ribbon + gift wrap
    this.bundlePrice = this.dataset.bundlePrice || '';
    
    // Type-specific properties
    if (this.isRibbon) {
      this.addonId = this.dataset.ribbonId;
      this.addonsInCart = parseInt(this.getAttribute('ribbons-in-cart') || '0');
      this.addonPrice = this.dataset.ribbonPrice || '';
      this.addonText = this.dataset.ribbonText || 'Add Gift Ribbon Wrapping';
      this.hasQuantityControl = true;
      this.hasNoteField = true;
      this.hasColorSelector = true;
      this.ribbonColors = this.parseRibbonColors();
      this.selectedColor = 'red'; // Default to Red
      this.currentQuantity = 0;
      this.isPrintOnly = this.dataset.printOnly === 'true'; // Check if cart has only prints
    } else if (this.isGiftWrap) {
      this.addonId = this.dataset.giftWrapId;
      this.addonsInCart = parseInt(this.getAttribute('gift-wraps-in-cart') || '0');
      this.addonPrice = this.dataset.giftWrapPrice || '';
      this.addonText = this.dataset.giftWrapText || 'Add Gift Wrapping';
      this.hasQuantityControl = true;
      this.hasNoteField = false; // Note is on ribbon
      this.hasColorSelector = false;
      this.hasThemeSelector = true;
      this.giftWrapThemes = this.parseGiftWrapThemes();
      this.selectedTheme = 'regular'; // Default to Regular
      this.currentQuantity = 0;
    } else {
      this.addonId = this.dataset.wallStripId;
      this.addonsInCart = parseInt(this.getAttribute('wall-strips-in-cart') || '0');
      this.addonPrice = this.dataset.wallStripPrice || '';
      this.addonText = this.dataset.wallStripText || 'Add Adhesive Wall Strips';
      this.hasQuantityControl = false;
      this.hasNoteField = false;
      this.hasColorSelector = false;
    }
    
    this.isEnabled = false;
    this.isBundleActive = false;
    this.init();
  }

  parseRibbonColors() {
    try {
      return JSON.parse(this.dataset.ribbonColors || '[]');
    } catch (e) {
      // Fallback to default colors if parsing fails
      return [
        { name: 'Red', value: 'red', hex: '#DC143C' },
        { name: 'Gold', value: 'gold', hex: '#FFD700' },
        { name: 'Pink', value: 'pink', hex: '#C0C0C0' },
        { name: 'Evergreen', value: 'green', hex: '#C0C0C0' },
        { name: 'Navy Blue', value: 'navy', hex: '#000080' },
        { name: 'Baby Blue', value: 'baby', hex: '#228B22' },
        { name: 'Pearl White', value: 'white', hex: '#FFFFFF' },
        { name: 'Black', value: 'black', hex: '#000000' },
        { name: 'Gold Stars', value: 'gold-stars', hex: '#8a4a22' },
        { name: 'Merry Christmas', value: 'merry-christmas', hex: '#b3261e' }
      ];
    }
  }

  parseGiftWrapThemes() {
    try {
      return JSON.parse(this.dataset.giftWrapThemes || '[]');
    } catch (e) {
      // Fallback to default themes if parsing fails
      return [
        { name: 'Regular', value: 'regular' },
        { name: 'Festive Theme', value: 'festive' }
      ];
    }
  }

  init() {
    this.createInterface();
    this.attachEventListeners();
    this.initializeFromCart();
    this.setupBundleMonitoring();
    this.setupQuantityChangeListener();
  }

  createInterface() {
    if (this.isMiniCart) {
      this.createMiniCartInterface();
    } else {
      this.createFullInterface();
    }
    
    this.addStyles();
    this.cacheElements();
  }

  createMiniCartInterface() {
    const checkboxId = `enable-${this.addonType}-mini`;
    
    if (this.hasQuantityControl) {
      // Ribbon or gift wrap with quantity control
      this.innerHTML = `
        <div class="mini-cart-addon mini-cart-${this.addonType}">
          <div class="addon-single-line">
            <div class="addon-toggle-compact">
              <input type="checkbox" id="${checkboxId}" class="addon-checkbox">
              <label for="${checkboxId}" class="addon-label-compact">
                <span class="addon__text">
                  ${this.addonText.replace('[PRICE]', this.addonPrice)}
                </span>
              </label>
            </div>
            
            <div class="addon-quantity-compact" style="display: none;">
              <div class="quantity-input-mini">
                <button type="button" class="qty-btn-mini qty-minus">-</button>
                <input type="number" id="${this.addonType}-qty-mini" class="addon-qty-input-mini" 
                       min="1" max="${this.itemsInCart}" value="1">
                <button type="button" class="qty-btn-mini qty-plus">+</button>
              </div>
            </div>
          </div>
          
          ${this.hasColorSelector ? `
          <div class="addon-color-selector-compact" style="display: none; margin-top: 8px;">
            <label class="color-label-mini">Ribbon Colour:</label>
            <select class="color-select-mini">
              ${this.ribbonColors.map(color => 
                `<option value="${color.value}" ${color.value === 'red' ? 'selected' : ''}>${color.name}</option>`
              ).join('')}
            </select>
          </div>
          ` : ''}
          
          ${this.hasThemeSelector ? `
          <div class="addon-theme-selector-compact" style="display: none; margin-top: 8px;">
            <label class="theme-label-mini">Gift Wrap Theme:</label>
            <select class="theme-select-mini">
              ${this.giftWrapThemes.map(theme => 
                `<option value="${theme.value}" ${theme.value === 'regular' ? 'selected' : ''}>${theme.name}</option>`
              ).join('')}
            </select>
          </div>
          ` : ''}
          
          ${this.isRibbon && this.bundleId ? `
          <div class="bundle-promo-button-mini-wrapper">
            <button type="button" class="bundle-promo-button-mini">
              Select both for ${this.bundlePrice}! 💡
            </button>
          </div>
          ` : ''}
          
          <div class="bundle-message-mini" style="display: none;"></div>
          
          <div class="loading-overlay hidden">
            <span>Updating...</span>
          </div>
        </div>
      `;
    } else {
      // Wall strips without quantity control
      this.innerHTML = `
        <div class="mini-cart-addon mini-cart-${this.addonType}">
          <div class="addon-toggle-compact">
            <input type="checkbox" id="${checkboxId}" class="addon-checkbox">
            <label for="${checkboxId}" class="addon-label-compact">
              <span class="addon__text">
                ${this.addonText.replace('[PRICE]', this.addonPrice)}
              </span>
            </label>
          </div>
          
          <div class="loading-overlay hidden">
            <span>Updating...</span>
          </div>
        </div>
      `;
    }
  }

  createFullInterface() {
    const checkboxId = `enable-${this.addonType}`;
    const baseClass = this.isRibbon ? 'ribbon-wrapping' :
                     this.isGiftWrap ? 'gift-wrapping-addon' :
                     'adhesive-wall-strips';

    // Icons for each addon type
    const icons = {
      ribbon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3a4 4 0 0 0-4 4c0 2 2 4 4 4s4-2 4-4a4 4 0 0 0-4-4z"/><path d="M8 11v10l4-2 4 2V11"/></svg>`,
      giftWrap: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="8" width="18" height="14" rx="2"/><path d="M12 8v14"/><path d="M3 12h18"/><path d="M12 3c-1.5 0-3 .5-3 2s1.5 3 3 3 3-1 3-3-.5-2-3-2z"/></svg>`,
      wallStrips: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>`
    };

    const icon = this.isRibbon ? icons.ribbon : this.isGiftWrap ? icons.giftWrap : icons.wallStrips;

    // Title and subtitle for each type
    const titles = {
      ribbon: {
        title: 'Gift Ribbon',
        subtitle: this.isPrintOnly
          ? 'Your print will arrive beautifully rolled with ribbon'
          : 'Add an elegant ribbon finish'
      },
      giftWrap: { title: 'Gift Wrapping', subtitle: 'Beautiful presentation ready' },
      wallStrips: { title: 'Wall Strips', subtitle: 'Damage-free mounting' }
    };

    const titleData = this.isRibbon ? titles.ribbon : this.isGiftWrap ? titles.giftWrap : titles.wallStrips;

    let detailsSection = '';

    if (this.isRibbon) {
      detailsSection = `
        <div class="addon-details-section" style="display: none;">
          <div class="addon-main-content">
            <div class="color-selector-section">
              <label for="ribbon-color-select">Ribbon Colour</label>
              <div class="color-selector-wrapper">
                <select id="ribbon-color-select" class="color-select">
                  ${this.ribbonColors.map(color =>
                    `<option value="${color.value}">${color.name}</option>`
                  ).join('')}
                </select>
              </div>
              <p class="color-helper">
                <a href="/products/gift-wrapping-ribbon" target="_blank">View all ribbon colours →</a>
              </p>
            </div>

            <div class="addon-note-section">
              <label for="gift-note">
                Gift Message
                <span class="gift-note-tooltip">
                  <svg class="gift-note-info-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>
                    <path d="M8 7v4M8 5v.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  </svg>
                  <span class="gift-note-tooltip-content">
                    <img src="https://cdn.shopify.com/s/files/1/0710/5278/7987/files/Skylit-Studio-gift-message-new.svg?v=1764438383"
                         alt="Gift note example" loading="lazy"/>
                    <span class="tooltip-caption">Example of how your gift note will appear</span>
                  </span>
                </span>
              </label>
              <textarea id="gift-note" class="addon-note-input"
                        placeholder="Write a personal message for the recipient..." rows="2"></textarea>
              <div class="gift-note-actions">
                <button type="button" class="gift-note-save-btn">
                  <span class="save-btn-text">Save Message</span>
                  <span class="save-btn-saving">Saving...</span>
                  <span class="save-btn-saved">✓ Saved</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      `;
    } else if (this.isGiftWrap) {
      detailsSection = `
        <div class="addon-details-section" style="display: none;">
          <div class="addon-main-content">
            <div class="theme-selector-section">
              <label for="gift-wrap-theme-select">Gift Wrap Design</label>
              <div class="theme-selector-wrapper">
                <select id="gift-wrap-theme-select" class="theme-select">
                  ${this.giftWrapThemes.map(theme =>
                    `<option value="${theme.value}" ${theme.value === 'regular' ? 'selected' : ''}>${theme.name}</option>`
                  ).join('')}
                </select>
              </div>
            </div>

            <div class="addon-description">
              <p>Premium gift wrapping with elegant paper. Style may vary based on availability.</p>
            </div>
          </div>
        </div>
      `;
    } else {
      detailsSection = `
        <div class="addon-info-section" style="display: none;">
          <div class="addon-main-content">
            <div class="addon-description">
              <p>Damage-free mounting strips for easy wall display. Works on most surfaces including painted walls, tiles, and wood.</p>
            </div>
          </div>
        </div>
      `;
    }

    this.innerHTML = `
      <div class="${baseClass}">
        <div class="addon-toggle">
          <input type="checkbox" id="${checkboxId}" class="addon-checkbox">
          <span class="addon-toggle-switch"></span>
          <div class="addon-icon">${icon}</div>
          <label for="${checkboxId}" class="addon-label">
            <span class="addon-label-title">
              ${titleData.title}
              <span class="addon-price-badge">${this.addonPrice}</span>
            </span>
            <span class="addon-label-price">${titleData.subtitle}</span>
          </label>
        </div>

        ${detailsSection}

        <div class="bundle-message" style="display: none;"></div>

        <div class="loading-overlay hidden">
          <span>Updating cart...</span>
        </div>
      </div>
    `;
  }

  cacheElements() {
    this.checkbox = this.querySelector('.addon-checkbox');
    this.loadingOverlay = this.querySelector('.loading-overlay');
    this.bundleMessage = this.querySelector('.bundle-message, .bundle-message-mini');
    
    if (!this.checkbox || !this.loadingOverlay) {
      console.error('Required elements not found for', this.addonType);
      return;
    }
    
    if (this.hasQuantityControl) {
      this.detailsSection = this.querySelector('.addon-details-section, .addon-quantity-compact');
      this.quantityInput = this.querySelector('.addon-qty-input, .addon-qty-input-mini');
      this.minusBtn = this.querySelector('.qty-minus');
      this.plusBtn = this.querySelector('.qty-plus');
    }
    
    if (this.hasColorSelector) {
      this.colorSelector = this.querySelector('.color-select, .color-select-mini');
      this.colorSelectorSection = this.querySelector('.addon-color-selector-compact, .color-selector-section');
      this.colorPreview = this.querySelector('.color-preview');
      this.colorSwatch = this.querySelector('.color-swatch');
    }

    if (this.hasThemeSelector) {
      this.themeSelector = this.querySelector('.theme-select, .theme-select-mini');
      this.themeSelectorSection = this.querySelector('.addon-theme-selector-compact, .theme-selector-section');
    }
    
    if (this.hasNoteField) {
      this.noteInput = this.querySelector('.addon-note-input');
    }
    
    if (!this.hasQuantityControl && !this.isWallStrips) {
      this.infoSection = this.querySelector('.addon-info-section');
    }
  }

  addStyles() {
    const styleId = this.isMiniCart ? `mini-cart-${this.addonType}-styles-v2` : `${this.addonType}-styles-v2`;
    
    if (document.getElementById(styleId)) return;
    
    const style = document.createElement('style');
    style.id = styleId;
    
    if (this.isMiniCart) {
      style.textContent = this.getMiniCartStyles();
    } else {
      style.textContent = this.getFullStyles();
    }
    
    document.head.appendChild(style);
  }

  getMiniCartStyles() {
    const bgColor = this.isRibbon ? '#fff5f5' : this.isGiftWrap ? '#f9f9f9' : '#f0f8ff';
    
    return `
      .mini-cart-addon {
        border-top: 1px solid #e0e0e0;
        background: ${bgColor};
        border-radius: 6px;
        position: relative;
        margin-left: -10px;
        margin-right: -10px;
        padding: 10px 12px;
        margin-top: 8px;
      }

      .addon-single-line {
        display: flex;
        align-items: center;
        gap: 12px;
        flex-wrap: nowrap;
      }

      .addon-toggle-compact {
        ${this.hasQuantityControl ? 'flex: 1; min-width: 0;' : ''}
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .addon-toggle-compact input[type="checkbox"] {
        flex-shrink: 0;
        vertical-align: baseline;
        margin-top: 1px;
      }

      .addon-label-compact {
        font-size: 12px;
        cursor: pointer;
        display: flex;
        align-items: center;
        line-height: 1.3;
        font-weight: 500;
      }

      .addon__text {
        font-size: 1.1rem;
        padding-left: 5px;
      }

      .addon-quantity-compact {
        flex-shrink: 0;
      }

      .quantity-input-mini {
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .qty-btn-mini {
        width: 22px;
        height: 22px;
        border: 1px solid #ddd;
        background: white;
        cursor: pointer;
        border-radius: 3px;
        font-weight: bold;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        color: #555;
        transition: all 0.2s ease;
        flex-shrink: 0;
      }

      .qty-btn-mini:hover:not(:disabled) {
        background: #f5f5f5;
        border-color: #ccc;
      }

      .qty-btn-mini:disabled {
        opacity: 0.4;
        cursor: not-allowed;
        background: #f9f9f9;
      }

      .addon-qty-input-mini {
        width: 35px;
        height: 22px;
        text-align: center;
        border: 1px solid #ddd;
        border-radius: 3px;
        padding: 0 2px;
        font-size: 11px;
        font-weight: 500;
        flex-shrink: 0;
      }

      .addon-color-selector-compact {
        padding-left: 28px;
      }

      .color-label-mini {
        font-size: 11px;
        font-weight: 500;
        margin-right: 6px;
        color: #555;
      }

      .color-select-mini {
        font-size: 11px;
        padding: 4px 8px;
        border: 1px solid #ddd;
        border-radius: 3px;
        background: white;
        cursor: pointer;
      }

      .addon-theme-selector-compact {
        padding-left: 28px;
      }

      .theme-label-mini {
        font-size: 11px;
        font-weight: 500;
        margin-right: 6px;
        color: #555;
      }

      .theme-select-mini {
        font-size: 11px;
        padding: 4px 8px;
        border: 1px solid #ddd;
        border-radius: 3px;
        background: white;
        cursor: pointer;
      }

      .bundle-message-mini {
        margin-top: 8px;
        padding: 8px;
        background: #d4edda;
        border: 1px solid #c3e6cb;
        border-radius: 4px;
        font-size: 11px;
        font-weight: 600;
        color: #155724;
        text-align: center;
      }

      .bundle-promo-button-mini-wrapper {
        text-align: center;
        margin-top: 8px;
      }

      .bundle-promo-button-mini {
        display: inline-block;
        margin: 0 auto;
        padding: 8px 16px;
        background: linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%);
        border: 2px solid #fdcb6e;
        border-radius: 6px;
        font-size: 11px;
        font-weight: 600;
        color: #2d3436;
        text-align: center;
        cursor: pointer;
        transition: all 0.2s ease;
        width: auto;
        max-width: 100%;
      }

      .bundle-promo-button-mini:hover {
        background: linear-gradient(135deg, #fdcb6e 0%, #ffeaa7 100%);
        transform: translateY(-1px);
      }

      .bundle-promo-button-mini:active {
        transform: translateY(0);
      }

      .mini-cart-addon .loading-overlay {
        position: absolute !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        width: 100% !important;
        height: 100% !important;
        background: rgba(255, 255, 255, 0.95) !important;
        display: flex !important;
        align-items: center;
        justify-content: center;
        border-radius: 6px;
        font-size: 11px;
        color: #666;
        font-weight: 500;
        padding: 0 !important;
        visibility: visible !important;
        white-space: nowrap;
        z-index: 999 !important;
        box-sizing: border-box;
      }

      .mini-cart-addon .loading-overlay.hidden {
        display: none !important;
      }

      @media (max-width: 768px) {
        .addon__text {
          font-size: 1rem;
        }
      }
    `;
  }

  getFullStyles() {
    const accentColor = '#C0821D';
    const brandGold = '#FFB503';

    return `
      /* ================================================
         MODERN GIFT ADD-ONS REDESIGN - Skylit Studio
         ================================================ */

      /* Base card style - unified cream theme */
      .ribbon-wrapping,
      .gift-wrapping-addon,
      .adhesive-wall-strips {
        background: linear-gradient(135deg, #fefcf8 0%, #faf6f0 100%);
        border: 1px solid rgba(192, 130, 29, 0.15);
        border-radius: 12px;
        padding: 0;
        margin: 12px 0;
        position: relative;
        overflow: hidden;
        transition: all 0.3s ease;
        box-shadow: 0 2px 8px rgba(0,0,0,0.04);
        isolation: isolate;
      }

      .ribbon-wrapping:hover,
      .gift-wrapping-addon:hover,
      .adhesive-wall-strips:hover {
        box-shadow: 0 4px 16px rgba(192, 130, 29, 0.12);
        border-color: rgba(192, 130, 29, 0.25);
      }

      /* Custom toggle switch header */
      .addon-toggle {
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 16px 20px;
        cursor: pointer;
        transition: background 0.2s ease;
        margin: 0;
        position: relative;
        z-index: 1;
      }

      .addon-toggle:hover {
        background: rgba(192, 130, 29, 0.04);
      }

      /* Hide default checkbox, use custom toggle */
      .addon-toggle input[type="checkbox"] {
        position: absolute;
        opacity: 0;
        width: 0;
        height: 0;
      }

      /* Custom toggle switch */
      .addon-toggle-switch {
        position: relative;
        width: 48px;
        height: 26px;
        background: #d1d5db;
        border-radius: 26px;
        transition: all 0.3s ease;
        flex-shrink: 0;
        cursor: pointer;
      }

      .addon-toggle-switch::after {
        content: '';
        position: absolute;
        top: 3px;
        left: 3px;
        width: 20px;
        height: 20px;
        background: white;
        border-radius: 50%;
        transition: all 0.3s ease;
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
      }

      .addon-toggle input:checked + .addon-toggle-switch {
        background: ${brandGold};
      }

      .addon-toggle input:checked + .addon-toggle-switch::after {
        transform: translateX(22px);
      }

      /* Icon container */
      .addon-icon {
        width: 40px;
        height: 40px;
        background: rgba(192, 130, 29, 0.1);
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        cursor: pointer;
      }

      .addon-icon svg {
        width: 22px;
        height: 22px;
        color: ${accentColor};
      }

      /* Label styling */
      .addon-label {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 2px;
        cursor: pointer;
      }

      .addon-label-title {
        font-weight: 600;
        font-size: 15px;
        color: #1f2937;
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .addon-label-price {
        font-size: 13px;
        color: #6b7280;
        font-weight: 400;
      }

      .addon-price-badge {
        background: ${brandGold};
        color: #1f2937;
        font-size: 12px;
        font-weight: 600;
        padding: 2px 8px;
        border-radius: 12px;
      }

      /* Expanded details section */
      .addon-details-section,
      .addon-info-section {
        padding: 0 20px 20px 20px;
        background: rgba(255,255,255,0.6);
        border-top: 1px solid rgba(192, 130, 29, 0.1);
        animation: slideDown 0.3s ease;
      }

      @keyframes slideDown {
        from { opacity: 0; transform: translateY(-10px); }
        to { opacity: 1; transform: translateY(0); }
      }

      .addon-main-content {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 20px;
        padding-top: 16px;
      }

      @media (max-width: 600px) {
        .addon-main-content {
          grid-template-columns: 1fr;
        }
      }

      /* Form field styling */
      .addon-field {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }

      .addon-field-label {
        font-size: 13px;
        font-weight: 600;
        color: #374151;
        display: flex;
        align-items: center;
        gap: 6px;
      }

      .addon-field-label svg {
        width: 14px;
        height: 14px;
        color: ${accentColor};
      }

      /* Quantity controls - hide for now as not being used */
      .quantity-controls {
        display: none;
      }

      /* Custom select styling */
      .color-select,
      .theme-select {
        width: 100%;
        padding: 10px 14px;
        border: 1px solid #d1d5db;
        border-radius: 8px;
        font-size: 14px;
        background: white;
        cursor: pointer;
        transition: all 0.2s ease;
        appearance: none;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 12px center;
        padding-right: 36px;
      }

      .color-select:hover,
      .theme-select:hover {
        border-color: ${accentColor};
      }

      .color-select:focus,
      .theme-select:focus {
        outline: none;
        border-color: ${brandGold};
        box-shadow: 0 0 0 3px rgba(255, 181, 3, 0.15);
      }

      .color-selector-section,
      .theme-selector-section {
        margin: 0;
        display: none; /* Hidden — only one option available */
      }

      .color-selector-wrapper,
      .theme-selector-wrapper {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }

      .color-selector-section label,
      .theme-selector-section label {
        font-size: 13px;
        font-weight: 600;
        color: #374151;
        margin-bottom: 0;
      }

      .color-helper {
        font-size: 12px;
        color: #6b7280;
        font-style: normal;
        margin-top: 6px;
      }

      .color-helper a {
        color: ${accentColor};
        text-decoration: none;
        font-weight: 500;
      }

      .color-helper a:hover {
        text-decoration: underline;
      }

      .color-preview {
        display: none;
      }

      /* Gift note section */
      .addon-note-section {
        grid-column: 1 / -1;
        margin-top: 4px;
        display: none; /* Hidden — gift message now in cart items area */
      }

      .addon-note-section label {
        font-size: 13px;
        font-weight: 600;
        color: #374151;
        margin-bottom: 6px;
        display: flex;
        align-items: center;
        gap: 6px;
      }

      .gift-note-tooltip {
        position: relative;
        display: inline-flex;
        cursor: help;
      }

      .gift-note-info-icon {
        color: #9ca3af;
        transition: color 0.2s;
      }

      .gift-note-tooltip:hover .gift-note-info-icon {
        color: ${accentColor};
      }

      .gift-note-tooltip-content {
        display: none;
        position: absolute;
        bottom: calc(100% + 8px);
        left: 50%;
        transform: translateX(-50%);
        background: white;
        border-radius: 8px;
        box-shadow: 0 10px 40px rgba(0,0,0,0.15);
        padding: 12px;
        z-index: 100;
        width: 200px;
      }

      .gift-note-tooltip:hover .gift-note-tooltip-content {
        display: block;
      }

      .gift-note-tooltip-content img {
        width: 100%;
        border-radius: 4px;
      }

      .tooltip-caption {
        display: block;
        font-size: 11px;
        color: #6b7280;
        text-align: center;
        margin-top: 6px;
      }

      .addon-note-input {
        width: 100%;
        padding: 10px 12px;
        border: 1px solid #d1d5db;
        border-radius: 8px;
        resize: none;
        font-family: inherit;
        font-size: 14px;
        min-height: 60px;
        transition: all 0.2s ease;
      }

      .addon-note-input:hover {
        border-color: ${accentColor};
      }

      .addon-note-input:focus {
        outline: none;
        border-color: ${brandGold};
        box-shadow: 0 0 0 3px rgba(255, 181, 3, 0.15);
      }

      .addon-note-input::placeholder {
        color: #9ca3af;
      }

      /* Gift note save button */
      .gift-note-actions {
        display: flex;
        justify-content: flex-start;
        margin-top: 8px;
      }

      .gift-note-save-btn {
        padding: 8px 16px;
        font-size: 13px;
        font-weight: 600;
        border: 1px solid ${brandGold};
        border-radius: 6px;
        background: transparent;
        color: ${brandGold};
        cursor: pointer;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        gap: 6px;
      }

      .gift-note-save-btn:hover {
        background: ${brandGold};
        color: white;
      }

      .gift-note-save-btn .save-btn-saving,
      .gift-note-save-btn .save-btn-saved {
        display: none;
      }

      .gift-note-save-btn.saving .save-btn-text,
      .gift-note-save-btn.saving .save-btn-saved {
        display: none;
      }

      .gift-note-save-btn.saving .save-btn-saving {
        display: inline;
      }

      .gift-note-save-btn.saved {
        background: #10b981;
        border-color: #10b981;
        color: white;
      }

      .gift-note-save-btn.saved .save-btn-text,
      .gift-note-save-btn.saved .save-btn-saving {
        display: none;
      }

      .gift-note-save-btn.saved .save-btn-saved {
        display: inline;
      }

      /* Description text */
      .addon-description {
        grid-column: 1 / -1;
      }

      .addon-description p {
        margin: 0;
        font-size: 13px;
        color: #6b7280;
        line-height: 1.5;
        background: rgba(192, 130, 29, 0.05);
        padding: 10px 14px;
        border-radius: 8px;
        border-left: 3px solid ${brandGold};
      }

      /* Wall strips images */
      .addon-images {
        display: flex;
        gap: 12px;
        flex-wrap: wrap;
        margin-top: 8px;
      }

      .addon-image {
        height: 80px;
        border-radius: 8px;
        overflow: hidden;
      }

      .addon-image img {
        height: 100%;
        width: auto;
        object-fit: contain;
      }

      /* Bundle message when active */
      .bundle-message {
        margin: 12px 20px 16px;
        padding: 12px 16px;
        background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
        border: 1px solid #6ee7b7;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 600;
        color: #065f46;
        text-align: center;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
      }

      .bundle-message::before {
        content: '✓';
        width: 20px;
        height: 20px;
        background: #065f46;
        color: white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
      }

      /* Loading overlay - using specific selectors to override global cart styles */
      .ribbon-wrapping > .loading-overlay,
      .gift-wrapping-addon > .loading-overlay,
      .adhesive-wall-strips > .loading-overlay {
        position: absolute !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        width: 100% !important;
        height: 100% !important;
        background: rgba(255, 255, 255, 0.95) !important;
        display: flex !important;
        align-items: center;
        justify-content: center;
        border-radius: 12px;
        font-weight: 500;
        z-index: 999 !important;
        color: ${accentColor};
        padding: 0 !important;
        visibility: visible !important;
        box-sizing: border-box;
      }

      .ribbon-wrapping > .loading-overlay span,
      .gift-wrapping-addon > .loading-overlay span,
      .adhesive-wall-strips > .loading-overlay span {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;
        white-space: nowrap;
      }

      .ribbon-wrapping > .loading-overlay span::before,
      .gift-wrapping-addon > .loading-overlay span::before,
      .adhesive-wall-strips > .loading-overlay span::before {
        content: '';
        width: 18px;
        height: 18px;
        border: 2px solid ${accentColor};
        border-top-color: transparent;
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
        flex-shrink: 0;
      }

      @keyframes spin {
        to { transform: rotate(360deg); }
      }

      .ribbon-wrapping > .loading-overlay.hidden,
      .gift-wrapping-addon > .loading-overlay.hidden,
      .adhesive-wall-strips > .loading-overlay.hidden {
        display: none !important;
      }

      /* Mobile responsive adjustments */
      @media (max-width: 768px) {
        .addon-toggle {
          padding: 14px 16px;
          gap: 12px;
        }

        .addon-icon {
          width: 36px;
          height: 36px;
        }

        .addon-icon svg {
          width: 18px;
          height: 18px;
        }

        .addon-label-title {
          font-size: 14px;
        }

        .addon-details-section,
        .addon-info-section {
          padding: 0 16px 16px 16px;
        }

        .addon-main-content {
          grid-template-columns: 1fr;
          gap: 16px;
        }
      }

      /* Gift note tooltip mobile fix */
      @media (max-width: 500px) {
        .gift-note-tooltip-content {
          position: fixed;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          max-width: 90vw;
          bottom: auto;
        }
      }
    `;
  }

  attachEventListeners() {
    // Main checkbox toggle
    this.checkbox.addEventListener('change', (e) => {
      if (e.target.checked) {
        this.enableAddon();
      } else {
        this.disableAddon();
      }
    });

    // Make toggle switch and icon clickable (for custom toggle UI)
    const toggleSwitch = this.querySelector('.addon-toggle-switch');
    const addonIcon = this.querySelector('.addon-icon');
    const addonToggle = this.querySelector('.addon-toggle');

    if (toggleSwitch) {
      toggleSwitch.addEventListener('click', (e) => {
        e.preventDefault();
        this.checkbox.checked = !this.checkbox.checked;
        this.checkbox.dispatchEvent(new Event('change'));
      });
    }

    if (addonIcon) {
      addonIcon.addEventListener('click', (e) => {
        e.preventDefault();
        this.checkbox.checked = !this.checkbox.checked;
        this.checkbox.dispatchEvent(new Event('change'));
      });
    }

    // Make the whole toggle row clickable (but not when clicking on expanded content)
    if (addonToggle) {
      addonToggle.style.cursor = 'pointer';
    }

    // Bundle promo button click
    const bundlePromoBtn = this.querySelector('.bundle-promo-button, .bundle-promo-button-mini');
    if (bundlePromoBtn) {
      bundlePromoBtn.addEventListener('click', () => {
        this.activateBundle();
      });
    }

    // Quantity controls
    if (this.hasQuantityControl) {
      this.attachQuantityListeners();
    }

    // Color selector for ribbon
    if (this.hasColorSelector && this.colorSelector) {
      this.attachColorListeners();
    }

    // Theme selector for gift wrap
    if (this.hasThemeSelector && this.themeSelector) {
      this.attachThemeListeners();
    }

    // Gift note
    if (this.hasNoteField && this.noteInput) {
      this.attachNoteListeners();
    }
  }

  attachQuantityListeners() {
    // Skip if quantity elements don't exist (new UI design)
    if (!this.minusBtn || !this.plusBtn || !this.quantityInput) {
      return;
    }

    this.minusBtn.addEventListener('click', () => {
      const currentValue = parseInt(this.quantityInput.value);
      if (currentValue > 1) {
        this.quantityInput.value = currentValue - 1;
        this.updateQuantity();
      }
    });

    this.plusBtn.addEventListener('click', () => {
      const currentValue = parseInt(this.quantityInput.value);
      if (currentValue < this.itemsInCart) {
        this.quantityInput.value = currentValue + 1;
        this.updateQuantity();
      }
    });

    let quantityTimeout;
    this.quantityInput.addEventListener('input', () => {
      clearTimeout(quantityTimeout);
      quantityTimeout = setTimeout(() => {
        this.updateQuantity();
      }, 500);
    });
  }

  attachColorListeners() {
    this.colorSelector.addEventListener('change', (e) => {
      this.selectedColor = e.target.value;
      this.updateColorPreview();
      
      // If we're in a bundle, just update attributes without breaking bundle
      if (this.isBundleActive) {
        console.log('Color changed while bundle active - updating bundle with new color');
        this.updateCart(); // This will maintain bundle in addToCart()
      } else {
        // Not in bundle, normal update
        this.updateCart();
      }
    });
  }

  updateColorPreview() {
    if (!this.colorPreview || !this.colorSwatch) return;
    
    const selectedColorObj = this.ribbonColors.find(c => c.value === this.selectedColor);
    
    if (selectedColorObj) {
      this.colorPreview.style.display = 'flex';
      this.colorSwatch.style.backgroundColor = selectedColorObj.hex;
      
      // Add white border for white color to make it visible
      if (selectedColorObj.value === 'white') {
        this.colorSwatch.style.border = '2px solid #999';
      } else {
        this.colorSwatch.style.border = '2px solid #ddd';
      }
    } else {
      this.colorPreview.style.display = 'none';
    }
  }

  attachThemeListeners() {
    this.themeSelector.addEventListener('change', (e) => {
      this.selectedTheme = e.target.value;
      
      // If we're in a bundle, just update attributes without breaking bundle
      if (this.isBundleActive) {
        console.log('Theme changed while bundle active - updating bundle with new theme');
        this.updateCart(); // This will maintain bundle in addToCart()
      } else {
        // Not in bundle, normal update
        this.updateCart();
      }
    });
  }

  attachNoteListeners() {
    // Cache save button
    this.noteSaveBtn = this.querySelector('.gift-note-save-btn');

    // Auto-save on input (with debounce) - but don't show visual feedback
    let noteTimeout;
    this.noteInput.addEventListener('input', () => {
      clearTimeout(noteTimeout);
      // Reset save button to default state when user types
      if (this.noteSaveBtn) {
        this.noteSaveBtn.classList.remove('saved');
      }
    });

    // Save button click handler
    if (this.noteSaveBtn) {
      this.noteSaveBtn.addEventListener('click', async () => {
        this.noteSaveBtn.classList.remove('saved');
        this.noteSaveBtn.classList.add('saving');
        this.noteSaveBtn.disabled = true;

        try {
          await this.updateNote();
          this.noteSaveBtn.classList.remove('saving');
          this.noteSaveBtn.classList.add('saved');

          // Reset to default after 3 seconds
          setTimeout(() => {
            this.noteSaveBtn.classList.remove('saved');
            this.noteSaveBtn.disabled = false;
          }, 3000);
        } catch (error) {
          console.error('Failed to save note:', error);
          this.noteSaveBtn.classList.remove('saving');
          this.noteSaveBtn.disabled = false;
        }
      });
    }

    // Setup legacy sync
    this.setupLegacyGiftNoteSync();
  }

  enableAddon() {
  this.isEnabled = true;

  if (this.detailsSection) {
    this.detailsSection.style.display = 'block';
    if (this.quantityInput) {
      this.quantityInput.value = Math.min(1, this.itemsInCart);
    }
    this.updateQuantityButtons();
  } else if (this.infoSection) {
    this.infoSection.style.display = 'block';
  }

  if (this.colorSelectorSection) {
    this.colorSelectorSection.style.display = 'block';
  }

  if (this.themeSelectorSection) {
    this.themeSelectorSection.style.display = 'block';
  }

  // Update bundle message on the other addon too
  this.updateBundleMessageOnBothAddons();

  // Check if we should use bundle - this will call updateCart() which calls addToCart()
  // NOTE: Removed duplicate addToCart() call that was causing quantity=2 bug
  this.checkAndUpdateBundle();
}

  activateBundle() {
    // This method is called when the bundle promo button is clicked
    // It enables both ribbon and gift wrap addons at once
    const ribbonAddon = document.querySelector('cart-addon[addon-type="ribbon"]');
    const giftWrapAddon = document.querySelector('cart-addon[addon-type="gift-wrap"]');
    
    if (ribbonAddon && giftWrapAddon) {
      // Enable both if not already enabled
      if (!ribbonAddon.isEnabled) {
        ribbonAddon.checkbox.checked = true;
        ribbonAddon.isEnabled = true;
        if (ribbonAddon.detailsSection) {
          ribbonAddon.detailsSection.style.display = 'block';
          if (ribbonAddon.quantityInput) {
            ribbonAddon.quantityInput.value = 1;
          }
          ribbonAddon.updateQuantityButtons();
        }
        if (ribbonAddon.colorSelectorSection) {
          ribbonAddon.colorSelectorSection.style.display = 'block';
        }
      }
      
      if (!giftWrapAddon.isEnabled) {
        giftWrapAddon.checkbox.checked = true;
        giftWrapAddon.isEnabled = true;
        if (giftWrapAddon.detailsSection) {
          giftWrapAddon.detailsSection.style.display = 'block';
          if (giftWrapAddon.quantityInput) {
            giftWrapAddon.quantityInput.value = 1;
          }
          giftWrapAddon.updateQuantityButtons();
        }
      }
      
      // Set bundle state on both
      ribbonAddon.isBundleActive = true;
      giftWrapAddon.isBundleActive = true;
      
      // Update bundle messages
      ribbonAddon.updateBundleMessage();
      giftWrapAddon.updateBundleMessage();
      
      // Trigger cart update from ribbon (it will handle the bundle)
      ribbonAddon.addToCart();
    }
  }

  async disableAddon() {
    this.isEnabled = false;

    // Hide UI
    if (this.detailsSection) {
      this.detailsSection.style.display = 'none';
    } else if (this.infoSection) {
      this.infoSection.style.display = 'none';
    }

    if (this.colorSelectorSection) {
      this.colorSelectorSection.style.display = 'none';
    }

    // Check if we're breaking a bundle
    const wasInBundle = this.isBundleActive;

    if (wasInBundle) {
      console.log('Breaking bundle - ' + this.addonType + ' disabled');

      // Find the other addon
      const otherAddon = this.isRibbon
        ? document.querySelector('cart-addon[addon-type="gift-wrap"]')
        : this.isGiftWrap
        ? document.querySelector('cart-addon[addon-type="ribbon"]')
        : null;

      // Mark bundle as inactive on both
      this.isBundleActive = false;
      if (otherAddon) {
        otherAddon.isBundleActive = false;
      }

      // Update messages immediately
      this.updateBundleMessage();
      if (otherAddon) {
        otherAddon.updateBundleMessage();
      }

      // Now handle the cart update
      // We need to: remove bundle, remove this addon, add other addon back as individual
      if (otherAddon && otherAddon.isEnabled) {
        console.log('Other addon (' + otherAddon.addonType + ') is still enabled, will re-add as individual');

        // Prevent concurrent cart operations
        if (CartAddonV2.cartOperationInProgress) {
          console.log(`[${this.addonType}] Cart operation in progress, waiting for disableAddon...`);
          await new Promise(resolve => {
            const checkLock = setInterval(() => {
              if (!CartAddonV2.cartOperationInProgress) {
                clearInterval(checkLock);
                resolve();
              }
            }, 50);
            setTimeout(() => {
              clearInterval(checkLock);
              resolve();
            }, 5000);
          });
        }

        CartAddonV2.cartOperationInProgress = true;

        // Build a complete cart update that does everything at once
        const sections = this.getSectionsToRender().map((section) => section.section);
        const cartUpdateUrl = window.theme?.routes?.cart_update_url || '/cart/update.js';

        this.showLoading();
        if (otherAddon) otherAddon.showLoading();

        try {
          let updates = {};
          let attributes = {};
          
          // Remove bundle
          if (this.bundleId) {
            updates[this.bundleId] = 0;
          }
          
          // Remove this addon
          updates[this.addonId] = 0;
          
          // Add other addon as individual
          const otherQty = otherAddon.hasQuantityControl 
            ? (parseInt(otherAddon.quantityInput?.value) || 1)
            : 1;
          updates[otherAddon.addonId] = otherQty;
          
          console.log('Cart update being sent:', {
            removingBundle: this.bundleId,
            removingAddon: this.addonId + ' (' + this.addonType + ')',
            addingAddon: otherAddon.addonId + ' (' + otherAddon.addonType + ')',
            addingQuantity: otherQty,
            updates: updates
          });
          
          // Build minimal attributes - keep only the essential options
          if (this.isRibbon) {
            // Ribbon being removed, gift wrap stays
            const giftNote = this.noteInput ? this.noteInput.value : '';
            const giftWrapTheme = otherAddon?.selectedTheme || 'regular';
            attributes = {
              '_addon_ribbon_color': '',
              '_addon_gift_note': giftNote, // Keep the note even if ribbon is removed
              '_addon_gift_wrap_theme': giftWrapTheme
            };
          } else if (this.isGiftWrap) {
            // Gift wrap being removed, ribbon stays
            const ribbonColor = otherAddon.selectedColor || '';
            const giftNote = otherAddon.noteInput ? otherAddon.noteInput.value : '';
            attributes = {
              '_addon_ribbon_color': ribbonColor,
              '_addon_gift_note': giftNote,
              '_addon_gift_wrap_theme': ''
            };
          }
          
          const body = JSON.stringify({
            updates: updates,
            attributes: attributes,
            sections: sections,
            sections_url: window.location.pathname
          });

          const response = await fetch(cartUpdateUrl, {
            ...fetchConfig(),
            body
          });
          
          const data = await response.json();
          
          console.log('Cart update response:', {
            ok: response.ok,
            itemsInCart: data.items?.map(i => ({ id: i.id, title: i.title, qty: i.quantity })),
            attributes: data.attributes
          });
          
          if (response.ok) {
            if (this.hasQuantityControl) {
              this.currentQuantity = 0;
            }
            if (otherAddon && otherAddon.hasQuantityControl) {
              otherAddon.currentQuantity = otherQty;
            }
            this.handleCartUpdate(data);
            if (otherAddon) {
              otherAddon.handleCartUpdate(data);
            }
          } else {
            throw new Error('Cart update failed');
          }
          
        } catch (error) {
          console.error('Bundle break error:', error);
          this.handleError();
          if (otherAddon) otherAddon.handleError();
        } finally {
          CartAddonV2.cartOperationInProgress = false;
          this.hideLoading();
          if (otherAddon) otherAddon.hideLoading();
        }
      } else {
        // No other addon or it's not enabled, just remove this one
        this.removeFromCart();
      }
    } else {
      // Not in a bundle, just remove this addon normally
      this.removeFromCart();
    }

    setTimeout(() => {
      this.updateBundleMessageOnBothAddons();
    }, 100);
  }

  updateQuantity() {
    if (!this.isEnabled || !this.hasQuantityControl || !this.quantityInput) return;

    const quantity = parseInt(this.quantityInput.value) || 1;
    const clampedQuantity = Math.max(1, Math.min(quantity, this.itemsInCart));

    if (quantity !== clampedQuantity) {
      this.quantityInput.value = clampedQuantity;
    }

    this.updateQuantityButtons();
    this.updateCart();
  }

  updateQuantityButtons() {
    if (!this.hasQuantityControl) return;
    // Skip if quantity elements don't exist (new UI design)
    if (!this.quantityInput || !this.minusBtn || !this.plusBtn) return;

    const quantity = parseInt(this.quantityInput.value) || 1;
    this.minusBtn.disabled = quantity <= 1;
    this.plusBtn.disabled = quantity >= this.itemsInCart;
  }

  checkAndUpdateBundle() {
    // Only ribbon and gift wrap participate in bundles
    if (!this.isRibbon && !this.isGiftWrap) {
      this.updateCart();
      return;
    }

    const ribbonAddon = document.querySelector('cart-addon[addon-type="ribbon"]');
    const giftWrapAddon = document.querySelector('cart-addon[addon-type="gift-wrap"]');

    if (!ribbonAddon || !giftWrapAddon) {
      this.updateCart();
      return;
    }

    const ribbonEnabled = ribbonAddon.isEnabled;
    const giftWrapEnabled = giftWrapAddon.isEnabled;

    const shouldUseBundle = ribbonEnabled && giftWrapEnabled;

    // Check if bundle state changed
    const previousBundleState = ribbonAddon.isBundleActive || giftWrapAddon.isBundleActive;
    const bundleStateChanged = shouldUseBundle !== previousBundleState;

    if (bundleStateChanged) {
      // Update bundle state on both addons BEFORE any cart operations
      ribbonAddon.isBundleActive = shouldUseBundle;
      giftWrapAddon.isBundleActive = shouldUseBundle;

      console.log('Bundle state changed to:', shouldUseBundle);

      // Update messages on both addons
      ribbonAddon.updateBundleMessage();
      giftWrapAddon.updateBundleMessage();

      if (shouldUseBundle) {
        // IMPORTANT: When activating bundle, only ribbon handles the cart update
        // This prevents race conditions where both addons try to add items
        console.log('Bundle activated - ribbon will handle cart update');
        ribbonAddon.updateCart();
        return; // Don't call this.updateCart() - ribbon handles it
      }
    }

    this.updateCart();
  }

  setupQuantityChangeListener() {
  // Listen for quantity changes in main cart
  document.addEventListener('cart:updated', (e) => {
    if (!e.detail || !e.detail.cart) return;
    this.syncWithCartState(e.detail.cart);
  });

  // Also listen for cart form submissions (when items are removed via cart page)
  const cartForm = document.querySelector('form[action*="/cart"]');
  if (cartForm) {
    const observer = new MutationObserver(() => {
      // Cart DOM changed, check if our addon is still in cart
      this.checkCartAndSync();
    });

    observer.observe(cartForm, {
      childList: true,
      subtree: true
    });
  }

  // Periodically check cart state to catch any missed updates
  setInterval(() => {
    this.checkCartAndSync();
  }, 2000);
 }

 async checkCartAndSync() {
  // Skip if page is unloading or hidden
  if (document.hidden || document.visibilityState === 'hidden') return;

  try {
    // Use XMLHttpRequest with /cart.json as fetch('/cart.js') can be blocked by some scripts
    const cart = await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', '/cart.json', true);
      xhr.timeout = 3000;
      xhr.onload = function() {
        if (xhr.status === 200) {
          resolve(JSON.parse(xhr.responseText));
        } else {
          reject(new Error('Cart fetch failed: ' + xhr.status));
        }
      };
      xhr.onerror = () => reject(new Error('XHR error'));
      xhr.ontimeout = () => reject(new Error('XHR timeout'));
      xhr.send();
    });

    this.syncWithCartState(cart);
  } catch (error) {
    // Don't reset state on error - just skip this sync cycle
    console.warn('Cart sync skipped:', error.message);
  }
 }

 syncWithCartState(cart) {
    if (!cart || !cart.items) return;

    const myAddonInCart = cart.items.find(item => item.id == this.addonId);
    const bundleInCart = this.bundleId ? cart.items.find(item => item.id == this.bundleId) : null;

    // Bundle is active if bundle product is in cart (detect from items, not attributes)
    const bundleActive = bundleInCart && bundleInCart.quantity > 0;

    // Check if this addon is active either individually OR as part of bundle
    const isActiveInCart = (myAddonInCart && myAddonInCart.quantity > 0) || bundleActive;

    if (!isActiveInCart) {
      // Addon is NOT in cart (neither individually nor in bundle)
      if (this.checkbox && this.checkbox.checked) {
        console.log(`${this.addonType} removed from cart, unchecking selection`);
        this.checkbox.checked = false;
        this.isEnabled = false;
        this.currentQuantity = 0;
        this.isBundleActive = false;

        if (this.detailsSection) {
          this.detailsSection.style.display = 'none';
        }
        if (this.infoSection) {
          this.infoSection.style.display = 'none';
        }
        if (this.colorSelectorSection) {
          this.colorSelectorSection.style.display = 'none';
        }
        if (this.themeSelectorSection) {
          this.themeSelectorSection.style.display = 'none';
        }

        // Update bundle state
        this.updateBundleMessageOnBothAddons();
      }
    } else {
      // Addon IS in cart - check if it's part of bundle
      if (bundleActive) {
        // Bundle is active, make sure checkbox is checked
        if (!this.checkbox.checked) {
          console.log(`${this.addonType} is part of active bundle, checking selection`);
          this.checkbox.checked = true;
          this.isEnabled = true;
          this.isBundleActive = true;
          this.currentQuantity = bundleInCart.quantity;

          if (this.detailsSection) {
            this.detailsSection.style.display = 'block';
          }
          if (this.colorSelectorSection) {
            this.colorSelectorSection.style.display = 'block';
          }
          if (this.themeSelectorSection) {
            this.themeSelectorSection.style.display = 'block';
          }
          if (this.quantityInput) {
            this.quantityInput.value = this.currentQuantity;
            this.updateQuantityButtons && this.updateQuantityButtons();
          }

          // Sync settings from cart attributes (check both old and new naming)
          if (this.isRibbon) {
            const ribbonColor = cart.attributes?._addon_ribbon_color || 'red';
            const giftNote = cart.attributes?._addon_gift_note || '';
            if (this.colorSelector) {
              this.colorSelector.value = ribbonColor;
              this.selectedColor = ribbonColor;
              this.updateColorPreview && this.updateColorPreview();
            }
            if (this.noteInput) {
              this.noteInput.value = giftNote;
            }
          } else if (this.isGiftWrap) {
            const giftWrapTheme = cart.attributes?._addon_gift_wrap_theme || 'regular';
            if (this.themeSelector) {
              this.themeSelector.value = giftWrapTheme;
              this.selectedTheme = giftWrapTheme;
            }
          }

          // Update bundle message
          this.updateBundleMessageOnBothAddons();
        }
      }
    }
  }

  updateBundleMessageOnBothAddons() {
    const ribbonAddon = document.querySelector('cart-addon[addon-type="ribbon"]');
    const giftWrapAddon = document.querySelector('cart-addon[addon-type="gift-wrap"]');

    if (ribbonAddon && ribbonAddon.updateBundleMessage) {
      ribbonAddon.updateBundleMessage();
    }
    if (giftWrapAddon && giftWrapAddon.updateBundleMessage) {
      giftWrapAddon.updateBundleMessage();
    }
  }

  updateBundleMessage() {
  // Find the shared bundle promo section (only in main cart)
  const bundlePromoSection = document.getElementById('bundle-promo-section');
  const bundlePromoButton = document.getElementById('bundle-promo-button');
  
  if (!bundlePromoSection || !bundlePromoButton) return;
  
  // Get both addons
  const ribbonAddon = document.querySelector('cart-addon[addon-type="ribbon"]');
  const giftWrapAddon = document.querySelector('cart-addon[addon-type="gift-wrap"]');
  
  if (!ribbonAddon || !giftWrapAddon) return;
  
  const ribbonEnabled = ribbonAddon.isEnabled;
  const giftWrapEnabled = giftWrapAddon.isEnabled;
  const bothEnabled = ribbonEnabled && giftWrapEnabled;
  
  // Always show the section (never hide it)
  bundlePromoSection.style.display = 'block';
  
  // Update button based on state
  if (bothEnabled && this.isBundleActive) {
    // State 3: Both selected - Bundle Active
    bundlePromoButton.innerHTML = '🎁 Bundle Applied! Ribbon + Gift Wrap for ' + this.bundlePrice;
    bundlePromoButton.style.background = 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)';
    bundlePromoButton.style.borderColor = '#c3e6cb';
    bundlePromoButton.style.color = '#155724';
    bundlePromoButton.style.cursor = 'default';
    bundlePromoButton.disabled = true;
    
  } else if (ribbonEnabled && !giftWrapEnabled) {
    // State 2a: Only ribbon selected - Prompt for gift wrap
    bundlePromoButton.innerHTML = '💡 Add Gift Wrap to save! Get both for ' + this.bundlePrice;
    bundlePromoButton.style.background = 'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%)';
    bundlePromoButton.style.borderColor = '#fdcb6e';
    bundlePromoButton.style.color = '#2d3436';
    bundlePromoButton.style.cursor = 'pointer';
    bundlePromoButton.disabled = false;
    
    // When clicked, enable gift wrap
    bundlePromoButton.onclick = () => {
      if (!giftWrapAddon.isEnabled) {
        giftWrapAddon.checkbox.checked = true;
        giftWrapAddon.enableAddon();
      }
    };
    
  } else if (!ribbonEnabled && giftWrapEnabled) {
    // State 2b: Only gift wrap selected - Prompt for ribbon
    bundlePromoButton.innerHTML = '💡 Add Ribbon Wrap to save! Get both for ' + this.bundlePrice;
    bundlePromoButton.style.background = 'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%)';
    bundlePromoButton.style.borderColor = '#fdcb6e';
    bundlePromoButton.style.color = '#2d3436';
    bundlePromoButton.style.cursor = 'pointer';
    bundlePromoButton.disabled = false;
    
    // When clicked, enable ribbon
    bundlePromoButton.onclick = () => {
      if (!ribbonAddon.isEnabled) {
        ribbonAddon.checkbox.checked = true;
        ribbonAddon.enableAddon();
      }
    };
    
  } else {
    // State 1: Neither selected - Prompt for bundle
    bundlePromoButton.innerHTML = '🎁 Save with our Bundle! Ribbon + Gift Wrap for ' + this.bundlePrice;
    bundlePromoButton.style.background = 'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%)';
    bundlePromoButton.style.borderColor = '#fdcb6e';
    bundlePromoButton.style.color = '#2d3436';
    bundlePromoButton.style.cursor = 'pointer';
    bundlePromoButton.disabled = false;
    
    // When clicked, enable both
    bundlePromoButton.onclick = () => {
      // Enable ribbon if not enabled
      if (!ribbonAddon.isEnabled) {
        ribbonAddon.checkbox.checked = true;
        ribbonAddon.isEnabled = true;
        if (ribbonAddon.detailsSection) {
          ribbonAddon.detailsSection.style.display = 'block';
          if (ribbonAddon.quantityInput) {
            ribbonAddon.quantityInput.value = 1;
          }
          ribbonAddon.updateQuantityButtons();
        }
        if (ribbonAddon.colorSelectorSection) {
          ribbonAddon.colorSelectorSection.style.display = 'block';
        }
      }
      
      // Enable gift wrap if not enabled
      if (!giftWrapAddon.isEnabled) {
        giftWrapAddon.checkbox.checked = true;
        giftWrapAddon.isEnabled = true;
        if (giftWrapAddon.detailsSection) {
          giftWrapAddon.detailsSection.style.display = 'block';
          if (giftWrapAddon.quantityInput) {
            giftWrapAddon.quantityInput.value = 1;
          }
          giftWrapAddon.updateQuantityButtons();
        }
      }
      
      // Set bundle state on both
      ribbonAddon.isBundleActive = true;
      giftWrapAddon.isBundleActive = true;
      
      // Update bundle messages
      ribbonAddon.updateBundleMessage();
      giftWrapAddon.updateBundleMessage();
      
      // Trigger cart update from ribbon (it will handle the bundle)
      ribbonAddon.addToCart();
    };
  }
  
  // Mini-cart bundle message (keep existing logic)
  const bundleMessageMini = this.querySelector('.bundle-message-mini');
  if (bundleMessageMini) {
    if (this.isBundleActive && this.bundleQuantity > 0) {
      bundleMessageMini.innerHTML = `
        <div class="bundle-active-mini">
          🎁 Bundle Applied! Ribbon + Gift Wrap for ${this.bundlePrice}
        </div>
      `;
      bundleMessageMini.style.display = 'block';
    } else {
      bundleMessageMini.style.display = 'none';
    }
  }
}

  async updateCart() {
    if (!this.isEnabled) {
      this.removeFromCart();
      return;
    }
    
    this.addToCart();
  }

  async addToCart() {
  // Prevent concurrent cart operations - wait if another operation is in progress
  if (CartAddonV2.cartOperationInProgress) {
    console.log(`[${this.addonType}] Cart operation in progress, waiting...`);
    // Wait for current operation to complete, then run this one
    await new Promise(resolve => {
      const checkLock = setInterval(() => {
        if (!CartAddonV2.cartOperationInProgress) {
          clearInterval(checkLock);
          resolve();
        }
      }, 50);
      // Timeout after 5 seconds to prevent deadlock
      setTimeout(() => {
        clearInterval(checkLock);
        resolve();
      }, 5000);
    });
  }

  CartAddonV2.cartOperationInProgress = true;
  this.showLoading();

  try {
    const sections = this.getSectionsToRender().map((section) => section.section);
    const cartChangeUrl = window.theme?.routes?.cart_change_url || '/cart/change.js';
    const cartUpdateUrl = window.theme?.routes?.cart_update_url || '/cart/update.js';
    const cartAddUrl = window.theme?.routes?.cart_add_url || '/cart/add.js';

    // Get current cart to find existing line items
    const cartResponse = await fetch('/cart.js');
    const currentCart = await cartResponse.json();
    
    // Determine what to add based on bundle status
    let attributes = {};
    
    if (this.isBundleActive && this.bundleId) {
      // Use bundle
      const ribbonAddon = document.querySelector('cart-addon[addon-type="ribbon"]');
      const giftWrapAddon = document.querySelector('cart-addon[addon-type="gift-wrap"]');
      
      const ribbonQty = ribbonAddon ? parseInt(ribbonAddon.quantityInput?.value || 1) : 1;
      const giftWrapQty = giftWrapAddon ? parseInt(giftWrapAddon.quantityInput?.value || 1) : 1;
      const bundleQuantity = Math.min(ribbonQty, giftWrapQty);
      
      const ribbonColor = ribbonAddon ? (ribbonAddon.selectedColor || 'red') : 'red';
      const ribbonColorName = ribbonAddon?.ribbonColors.find(c => c.value === ribbonColor)?.name || ribbonColor;
      const giftNote = ribbonAddon?.noteInput ? (ribbonAddon.noteInput.value || '') : '';
      const giftWrapTheme = giftWrapAddon ? (giftWrapAddon.selectedTheme || 'regular') : 'regular';
      const giftWrapThemeName = giftWrapAddon?.giftWrapThemes.find(t => t.value === giftWrapTheme)?.name || giftWrapTheme;
      
      // Find existing bundle item
      const existingBundleItem = currentCart.items.find(item => item.id == this.bundleId);
      
      // Remove individual items if they exist
      let updates = {};
      const existingRibbonItem = currentCart.items.find(item => item.id == ribbonAddon?.addonId);
      const existingGiftWrapItem = currentCart.items.find(item => item.id == giftWrapAddon?.addonId);
      
      if (existingRibbonItem) updates[ribbonAddon.addonId] = 0;
      if (existingGiftWrapItem) updates[giftWrapAddon.addonId] = 0;
      
      if (existingBundleItem) {
        // Update existing bundle item
        const lineItemKey = existingBundleItem.key;
        const changeBody = {
          id: lineItemKey,
          quantity: bundleQuantity,
          properties: {
            'Ribbon Colour': ribbonColorName || 'Red',
            'Gift Wrap Theme': giftWrapThemeName || 'Regular',
            'Gift Note': giftNote || ''
          },
          sections: sections,
          sections_url: window.location.pathname
        };
        
        const changeResponse = await fetch(cartChangeUrl, {
          ...fetchConfig(),
          body: JSON.stringify(changeBody)
        });
        
        const data = await changeResponse.json();
        
        // Also remove individual items if needed
        if (Object.keys(updates).length > 0) {
          await fetch(cartUpdateUrl, {
            ...fetchConfig(),
            body: JSON.stringify({ updates: updates })
          });
        }
        
        // Update attributes - only save what's needed for fulfillment
        // Note: Line item properties already have Ribbon Colour, Gift Wrap Theme, Gift Note
        // We only store minimal state for client-side UI persistence
        attributes = {
          '_addon_ribbon_color': ribbonColor,
          '_addon_gift_wrap_theme': giftWrapTheme,
          '_addon_gift_note': giftNote
        };

        await fetch(cartUpdateUrl, {
          ...fetchConfig(),
          body: JSON.stringify({
            attributes: attributes,
            sections: sections,
            sections_url: window.location.pathname
          })
        });

        if (changeResponse.ok) {
          this.handleCartUpdate(data);
        } else {
          throw new Error('Cart change failed');
        }
        
      } else {
        // Add new bundle item
        // ALWAYS remove individual items when switching to bundle (defensive approach)
        // This ensures we clean up even if timing caused them to be added
        const removeUpdates = {};
        if (ribbonAddon?.addonId) removeUpdates[ribbonAddon.addonId] = 0;
        if (giftWrapAddon?.addonId) removeUpdates[giftWrapAddon.addonId] = 0;

        console.log('Bundle: removing individual items:', removeUpdates);

        if (Object.keys(removeUpdates).length > 0) {
          await fetch(cartUpdateUrl, {
            ...fetchConfig(),
            body: JSON.stringify({ updates: removeUpdates })
          });
          // Small delay to ensure cart is updated before adding bundle
          await new Promise(resolve => setTimeout(resolve, 100));
        }

        const addBody = {
          items: [{
            id: this.bundleId,
            quantity: bundleQuantity,
            properties: {
              'Ribbon Colour': ribbonColorName || 'Red',
              'Gift Wrap Theme': giftWrapThemeName || 'Regular',
              'Gift Note': giftNote || ''
            }
          }],
          sections: sections,
          sections_url: window.location.pathname
        };

        console.log('Bundle: adding bundle item:', this.bundleId, 'qty:', bundleQuantity);

        const addResponse = await fetch(cartAddUrl, {
          ...fetchConfig(),
          body: JSON.stringify(addBody)
        });

        const data = await addResponse.json();

        // Only save minimal attributes for UI state - line item properties have the rest
        attributes = {
          '_addon_ribbon_color': ribbonColor,
          '_addon_gift_wrap_theme': giftWrapTheme,
          '_addon_gift_note': giftNote
        };

        await fetch(cartUpdateUrl, {
          ...fetchConfig(),
          body: JSON.stringify({
            attributes: attributes,
            sections: sections,
            sections_url: window.location.pathname
          })
        });

        if (addResponse.ok) {
          this.handleCartUpdate(data);
        } else {
          throw new Error('Failed to add bundle');
        }
      }
      
    } else {
      // Use individual items
      const quantity = (this.hasQuantityControl && this.quantityInput) ? (parseInt(this.quantityInput.value) || 1) : 1;
      
      // Find existing item
      const existingItem = currentCart.items.find(item => item.id == this.addonId);
      
      // Remove bundle if it exists
      const existingBundleItem = currentCart.items.find(item => item.id == this.bundleId);
      if (existingBundleItem) {
        await fetch(cartUpdateUrl, {
          ...fetchConfig(),
          body: JSON.stringify({ updates: { [this.bundleId]: 0 } })
        });
      }
      
      // Build properties
      let properties = {};
      
      if (this.isRibbon) {
        this.currentQuantity = quantity;
        const ribbonColorName = this.ribbonColors.find(c => c.value === this.selectedColor)?.name || this.selectedColor;
        const giftNote = this.noteInput ? this.noteInput.value || '' : '';
        
        properties = {
          'Ribbon Colour': ribbonColorName || 'Not selected'
        };
        
        if (giftNote) {
          properties['Gift Note'] = giftNote;
        }

        // Only save minimal attributes for UI state
        attributes = {
          '_addon_ribbon_color': this.selectedColor || '',
          '_addon_gift_note': giftNote
        };
      } else if (this.isGiftWrap) {
        this.currentQuantity = quantity;
        const giftWrapThemeName = this.giftWrapThemes.find(t => t.value === this.selectedTheme)?.name || this.selectedTheme;
        
        properties = {
          'Gift Wrap Theme': giftWrapThemeName || 'Regular'
        };

        // Only save minimal attributes for UI state
        attributes = {
          '_addon_gift_wrap_theme': this.selectedTheme || 'regular'
        };
      } else {
        // Wall strips don't need any attributes - cart item is sufficient
        attributes = {};
      }
      
      if (existingItem) {
        // Update existing item
        const lineItemKey = existingItem.key;
        const changeBody = {
          id: lineItemKey,
          quantity: quantity,
          properties: properties,
          sections: sections,
          sections_url: window.location.pathname
        };
        
        const changeResponse = await fetch(cartChangeUrl, {
          ...fetchConfig(),
          body: JSON.stringify(changeBody)
        });
        
        const data = await changeResponse.json();
        
        // Update attributes
        await fetch(cartUpdateUrl, {
          ...fetchConfig(),
          body: JSON.stringify({
            attributes: attributes,
            sections: sections,
            sections_url: window.location.pathname
          })
        });
        
        if (changeResponse.ok) {
          this.handleCartUpdate(data);
        } else {
          throw new Error('Cart change failed');
        }
        
      } else {
        // Add new item
        // For wall strips, use cart/update.js to SET quantity (prevents double-add issues)
        // For ribbon/gift wrap, use cart/add.js with properties
        if (this.isWallStrips) {
          // Wall strips: use update endpoint which SETS quantity instead of adding
          const updateBody = {
            updates: {
              [this.addonId]: quantity
            },
            attributes: attributes,
            sections: sections,
            sections_url: window.location.pathname
          };

          const updateResponse = await fetch(cartUpdateUrl, {
            ...fetchConfig(),
            body: JSON.stringify(updateBody)
          });

          const data = await updateResponse.json();

          if (updateResponse.ok) {
            this.handleCartUpdate(data);
          } else {
            throw new Error('Failed to add wall strips');
          }
        } else {
          // Ribbon/Gift wrap: use add endpoint with properties
          const addBody = {
            items: [{
              id: this.addonId,
              quantity: quantity,
              properties: properties
            }],
            sections: sections,
            sections_url: window.location.pathname
          };

          const addResponse = await fetch(cartAddUrl, {
            ...fetchConfig(),
            body: JSON.stringify(addBody)
          });

          const data = await addResponse.json();

          // Update attributes
          await fetch(cartUpdateUrl, {
            ...fetchConfig(),
            body: JSON.stringify({
              attributes: attributes,
              sections: sections,
              sections_url: window.location.pathname
            })
          });

          if (addResponse.ok) {
            this.handleCartUpdate(data);
          } else {
            throw new Error('Failed to add item');
          }
        }
      }
    }
    
  } catch (error) {
    console.error(`${this.addonType} update error:`, error);
    this.handleError();
  } finally {
    CartAddonV2.cartOperationInProgress = false;
    this.hideLoading();
  }
}

  async removeFromCart() {
    // Prevent concurrent cart operations
    if (CartAddonV2.cartOperationInProgress) {
      console.log(`[${this.addonType}] Cart operation in progress, waiting for removeFromCart...`);
      await new Promise(resolve => {
        const checkLock = setInterval(() => {
          if (!CartAddonV2.cartOperationInProgress) {
            clearInterval(checkLock);
            resolve();
          }
        }, 50);
        setTimeout(() => {
          clearInterval(checkLock);
          resolve();
        }, 5000);
      });
    }

    CartAddonV2.cartOperationInProgress = true;
    this.showLoading();

    try {
      const sections = this.getSectionsToRender().map((section) => section.section);
      const cartUpdateUrl = window.theme?.routes?.cart_update_url || '/cart/update.js';
      
      let updates = {};
      let attributes = {};
      
      updates[this.addonId] = 0;
      
      // Also remove bundle if this was part of one
      if (this.bundleId) {
        updates[this.bundleId] = 0;
      }
      
      // Only clear this addon's attributes (minimal set)
      if (this.isRibbon) {
        attributes = {
          '_addon_ribbon_color': '',
          '_addon_gift_note': ''
        };
      } else if (this.isGiftWrap) {
        attributes = {
          '_addon_gift_wrap_theme': ''
        };
      } else {
        // Wall strips have no attributes
        attributes = {};
      }
      
      const body = JSON.stringify({
        updates: updates,
        attributes: attributes,
        sections: sections,
        sections_url: window.location.pathname
      });

      const response = await fetch(cartUpdateUrl, {
        ...fetchConfig(),
        body
      });
      
      const data = await response.json();

      if (response.ok) {
        if (this.hasQuantityControl) {
          this.currentQuantity = 0;
        }
        // If no ribbon/wrap/bundle line item remains, fully clear addon attributes
        // so they don't leak into the order's note_attributes at checkout.
        await this.clearAddonAttributesIfNoGiftItems(data);
        this.handleCartUpdate(data);
        return data; // Return the data so we can use it in promises
      } else {
        throw new Error('Cart update failed');
      }

    } catch (error) {
      console.error(`${this.addonType} removal error:`, error);
      this.handleError();
    } finally {
      CartAddonV2.cartOperationInProgress = false;
      this.hideLoading();
    }
  }

  async clearAddonAttributesIfNoGiftItems(cartData) {
    const items = (cartData && cartData.items) || [];
    const GIFT_PROP_KEYS = ['Ribbon Colour', 'Gift Wrap Theme', 'Gift Note'];
    const hasGiftItem = items.some(item => {
      const props = item.properties;
      if (!props) return false;
      const keys = Array.isArray(props)
        ? props.map(p => (p && (p.name || p[0])) || '')
        : Object.keys(props);
      return keys.some(k => GIFT_PROP_KEYS.includes(k));
    });
    if (hasGiftItem) return;

    try {
      const cartUpdateUrl = window.theme?.routes?.cart_update_url || '/cart/update.js';
      await fetch(cartUpdateUrl, {
        ...fetchConfig(),
        body: JSON.stringify({
          attributes: {
            '_addon_ribbon_color': '',
            '_addon_gift_wrap_theme': '',
            '_addon_gift_note': ''
          }
        })
      });
    } catch (error) {
      console.error('Failed to clear stale addon attributes:', error);
    }
  }

  async updateNote() {
    if (!this.isEnabled || !this.noteInput) return;

    try {
      const cartUpdateUrl = window.theme?.routes?.cart_update_url || '/cart/update.js';
      const body = JSON.stringify({
        attributes: {
          '_addon_gift_note': this.noteInput.value || ''
        }
      });

      await fetch(cartUpdateUrl, {
        ...fetchConfig(),
        body
      });

    } catch (error) {
      console.error('Gift note update error:', error);
    }
  }

  setupBundleMonitoring() {
    // Listen for changes in other addons
    document.addEventListener('addon-state-changed', (e) => {
      if (e.detail.addonType !== this.addonType) {
        this.checkAndUpdateBundle();
      }
    });
  }

  initializeFromCart() {
    const cartAttributes = window.cart?.attributes || {};
    const cartItems = window.cart?.items || [];
    const ds = this.dataset || {};

    // Detect bundle and addon presence from cart items (more reliable than attributes)
    const bundleInCart = this.bundleId ? cartItems.find(item => item.id == this.bundleId) : null;
    const ribbonInCart = cartItems.find(item => item.id == ds.ribbonId);
    const giftWrapInCart = cartItems.find(item => item.id == ds.giftWrapId);
    const wallStripsInCart = cartItems.find(item => item.id == ds.wallStripId);

    // Bundle is active if the bundle product is in cart
    const bundleActive = bundleInCart && bundleInCart.quantity > 0;
    const bundleQty = bundleInCart ? bundleInCart.quantity : 0;

    // Read minimal attributes (new naming)
    const ribbonColor = ds.customRibbonColor || cartAttributes['_addon_ribbon_color'] || 'red';
    const giftNote = ds.customGiftNote || cartAttributes['_addon_gift_note'] || '';
    const giftWrapTheme = ds.customGiftWrapTheme || cartAttributes['_addon_gift_wrap_theme'] || 'regular';

    if (this.isRibbon) {
      // Ribbon is enabled if in bundle OR individually in cart
      const ribbonQuantity = bundleActive ? bundleQty : (ribbonInCart ? ribbonInCart.quantity : 0);
      const shouldEnable = bundleActive || (ribbonInCart && ribbonInCart.quantity > 0) || this.addonsInCart > 0;

      if (shouldEnable) {
        this.checkbox && (this.checkbox.checked = true);
        this.isEnabled = true;
        this.currentQuantity = ribbonQuantity || this.addonsInCart;

        if (this.detailsSection) {
          this.detailsSection.style.display = 'block';
          if (this.quantityInput) this.quantityInput.value = this.currentQuantity;
        }

        if (this.colorSelectorSection) this.colorSelectorSection.style.display = 'block';
        if (this.colorSelector) {
          this.colorSelector.value = ribbonColor;
          this.selectedColor = ribbonColor;
          this.updateColorPreview && this.updateColorPreview();
        }

        if (this.noteInput) this.noteInput.value = giftNote;
        this.updateQuantityButtons && this.updateQuantityButtons();
      }

      this.isBundleActive = !!bundleActive;
      this.bundleQuantity = bundleQty;
      this.updateBundleMessage && this.updateBundleMessage();

    } else if (this.isGiftWrap) {
      // Gift wrap is enabled if in bundle OR individually in cart
      const giftWrapQuantity = bundleActive ? bundleQty : (giftWrapInCart ? giftWrapInCart.quantity : 0);
      const shouldEnable = bundleActive || (giftWrapInCart && giftWrapInCart.quantity > 0) || this.addonsInCart > 0;

      if (shouldEnable) {
        this.checkbox && (this.checkbox.checked = true);
        this.isEnabled = true;
        this.currentQuantity = giftWrapQuantity || this.addonsInCart;

        if (this.detailsSection) {
          this.detailsSection.style.display = 'block';
          if (this.quantityInput) this.quantityInput.value = this.currentQuantity;
        }

        if (this.themeSelectorSection) this.themeSelectorSection.style.display = 'block';
        if (this.themeSelector) {
          this.themeSelector.value = giftWrapTheme;
          this.selectedTheme = giftWrapTheme;
        }

        this.updateQuantityButtons && this.updateQuantityButtons();
      }

      this.isBundleActive = !!bundleActive;
      this.bundleQuantity = bundleQty;
      this.updateBundleMessage && this.updateBundleMessage();

    } else {
      // Wall strips - just check if in cart
      if ((wallStripsInCart && wallStripsInCart.quantity > 0) || this.addonsInCart > 0) {
        this.checkbox && (this.checkbox.checked = true);
        this.isEnabled = true;
        if (this.infoSection) this.infoSection.style.display = 'block';
      }
    }
  }

  handleCartUpdate(data) {
    const cartUrl = window.theme?.routes?.cart_url || '/cart';

    if (document.body.classList.contains('template-cart')) {
      window.location.href = cartUrl;
      return;
    }

    // Make server cart available to new components
    window.cart = data;

    if (this.miniCart) {
      this.miniCart.renderContents(data);
    }

    // Detect bundle state from cart items (not attributes)
    const bundleInCart = this.bundleId ? data?.items?.find(item => item.id == this.bundleId) : null;
    const active = bundleInCart && bundleInCart.quantity > 0;

    const ribbonAddon = document.querySelector('cart-addon[addon-type="ribbon"]');
    const giftWrapAddon = document.querySelector('cart-addon[addon-type="gift-wrap"]');

    [ribbonAddon, giftWrapAddon].forEach(a => {
      if (!a) return;
      a.isBundleActive = !!active;
      a.updateBundleMessage();
    });

    // Sync checkbox states based on cart items (not attributes)
    if (ribbonAddon) {
      const ribbonInCart = data?.items?.find(item => item.id == ribbonAddon.addonId);
      const ribbonQuantity = active ? bundleInCart?.quantity : (ribbonInCart?.quantity || 0);
      const ribbonEnabled = active || (ribbonInCart && ribbonInCart.quantity > 0);

      if (ribbonEnabled) {
        if (!ribbonAddon.checkbox.checked) {
          ribbonAddon.checkbox.checked = true;
          ribbonAddon.isEnabled = true;
          if (ribbonAddon.detailsSection) {
            ribbonAddon.detailsSection.style.display = 'block';
          }
          if (ribbonAddon.colorSelectorSection) {
            ribbonAddon.colorSelectorSection.style.display = 'block';
          }
          if (ribbonAddon.quantityInput) {
            ribbonAddon.quantityInput.value = ribbonQuantity;
            ribbonAddon.updateQuantityButtons();
          }
        }
      } else if (ribbonAddon.checkbox.checked) {
        ribbonAddon.checkbox.checked = false;
        ribbonAddon.isEnabled = false;
        if (ribbonAddon.detailsSection) {
          ribbonAddon.detailsSection.style.display = 'none';
        }
        if (ribbonAddon.colorSelectorSection) {
          ribbonAddon.colorSelectorSection.style.display = 'none';
        }
      }
    }

    if (giftWrapAddon) {
      const giftWrapInCart = data?.items?.find(item => item.id == giftWrapAddon.addonId);
      const giftWrapQuantity = active ? bundleInCart?.quantity : (giftWrapInCart?.quantity || 0);
      const giftWrapEnabled = active || (giftWrapInCart && giftWrapInCart.quantity > 0);

      if (giftWrapEnabled) {
        if (!giftWrapAddon.checkbox.checked) {
          giftWrapAddon.checkbox.checked = true;
          giftWrapAddon.isEnabled = true;
          if (giftWrapAddon.detailsSection) {
            giftWrapAddon.detailsSection.style.display = 'block';
          }
          if (giftWrapAddon.themeSelectorSection) {
            giftWrapAddon.themeSelectorSection.style.display = 'block';
          }
          if (giftWrapAddon.quantityInput) {
            giftWrapAddon.quantityInput.value = giftWrapQuantity;
            giftWrapAddon.updateQuantityButtons();
          }
        }
      } else if (giftWrapAddon.checkbox.checked) {
        giftWrapAddon.checkbox.checked = false;
        giftWrapAddon.isEnabled = false;
        if (giftWrapAddon.detailsSection) {
          giftWrapAddon.detailsSection.style.display = 'none';
        }
        if (giftWrapAddon.themeSelectorSection) {
          giftWrapAddon.themeSelectorSection.style.display = 'none';
        }
      }
    }

    // Dispatch custom event
    const eventName = this.isRibbon ? 'ribbon-updated' :
                     this.isGiftWrap ? 'gift-wrap-updated' :
                     'wall-strips-updated';

    let eventDetail = { enabled: this.isEnabled };

    if (this.hasQuantityControl) {
      eventDetail.quantity = this.currentQuantity;
    }

    if (this.isRibbon) {
      eventDetail.color = this.selectedColor;
      eventDetail.note = this.noteInput ? this.noteInput.value : '';
    }

    this.dispatchEvent(new CustomEvent(eventName, {
      detail: eventDetail,
      bubbles: true
    }));

    // Notify other addons
    this.dispatchEvent(new CustomEvent('addon-state-changed', {
      detail: {
        addonType: this.addonType,
        enabled: this.isEnabled
      },
      bubbles: true
    }));
  }

  handleError() {
    this.checkbox.checked = this.addonsInCart > 0;

    if (this.hasQuantityControl) {
      if (this.quantityInput) {
        this.quantityInput.value = this.currentQuantity || 1;
      }
      if (this.currentQuantity > 0) {
        if (this.detailsSection) {
          this.detailsSection.style.display = 'block';
        }
      } else {
        if (this.detailsSection) {
          this.detailsSection.style.display = 'none';
        }
      }
    } else {
      if (this.addonsInCart > 0) {
        if (this.infoSection) {
          this.infoSection.style.display = 'block';
        }
      } else {
        if (this.infoSection) {
          this.infoSection.style.display = 'none';
        }
      }
    }
  }

  showLoading() {
    this.loadingOverlay.classList.remove('hidden');
  }

  hideLoading() {
    this.loadingOverlay.classList.add('hidden');
  }

  getSectionsToRender() {
    return [
      {
        id: 'mini-cart',
        section: 'mini-cart',
        selector: '.shopify-section'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'mobile-cart-icon-bubble',
        section: 'mobile-cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section'
      }
    ];
  }

  setupLegacyGiftNoteSync() {
    if (!this.isRibbon) return;
    
    const legacyGiftNoteTextarea = document.querySelector('#Gift-note, textarea[name="attributes[_addon_gift_note]"]');
    
    if (legacyGiftNoteTextarea) {
      let legacyNoteTimeout;
      legacyGiftNoteTextarea.addEventListener('input', () => {
        clearTimeout(legacyNoteTimeout);
        legacyNoteTimeout = setTimeout(() => {
          this.syncFromLegacyNote(legacyGiftNoteTextarea.value);
        }, 500);
      });

      if (legacyGiftNoteTextarea.value && this.isEnabled) {
        this.syncFromLegacyNote(legacyGiftNoteTextarea.value);
      }
    }

    const giftNotePopup = document.querySelector('details:has(#Gift-note)');
    if (giftNotePopup) {
      giftNotePopup.addEventListener('toggle', () => {
        if (giftNotePopup.open) {
          this.syncToLegacyNote();
        }
      });
    }
  }

  syncFromLegacyNote(noteValue) {
    if (this.noteInput) {
      this.noteInput.value = noteValue;
    }
    this.updateNoteOnly(noteValue);
  }

  syncToLegacyNote() {
    const legacyGiftNoteTextarea = document.querySelector('#Gift-note, textarea[name="attributes[_addon_gift_note]"]');
    
    if (legacyGiftNoteTextarea) {
      const currentNote = this.noteInput ? this.noteInput.value : (this.dataset.customGiftNote || '');
      
      if (currentNote && currentNote !== legacyGiftNoteTextarea.value) {
        legacyGiftNoteTextarea.value = currentNote;
      }
    }
  }

  async updateNoteOnly(noteValue) {
    if (!this.isEnabled) return;

    try {
      const cartUpdateUrl = window.theme?.routes?.cart_update_url || '/cart/update.js';
      const body = JSON.stringify({
        attributes: {
          '_addon_gift_note': noteValue || ''
        }
      });

      await fetch(cartUpdateUrl, {
        ...fetchConfig(),
        body
      });

    } catch (error) {
      console.error('Gift note sync error:', error);
    }
  }
}

// Register the unified component
if (!customElements.get('cart-addon')) {
  try {
    customElements.define('cart-addon', CartAddonV2);
  } catch (error) {
    console.error('Failed to register cart-addon component:', error);
  }
}

// Backward compatibility - these will work with the new component
if (!customElements.get('independent-gift-wrap')) {
  try {
    customElements.define('independent-gift-wrap', class extends CartAddonV2 {
      constructor() {
        super();
        this.setAttribute('addon-type', 'ribbon');
      }
    });
  } catch (error) {
    console.error('Failed to register independent-gift-wrap component:', error);
  }
}